<script setup lang="ts">
import {
  CalendarOutlined,
  EnvironmentOutlined,
  PlusOutlined,
  UnorderedListOutlined,
} from '@ant-design/icons-vue'
import { message } from 'ant-design-vue'
import type { Dayjs } from 'dayjs'
import { computed, h, onMounted, ref, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import type { Booking, Property } from '@/models'
import { createBooking, deleteBooking, fetchBookings, updateBooking } from '@/services/bookings'
import { fetchOtaBookings } from '@/services/otaSync'
import { getPreference, upsertPreference } from '@/services/preferences'
import { fetchProperties } from '@/services/properties'
import { useAuthStore } from '@/stores/auth'
import BookingFormDialog from '@/views/bookings/BookingFormDialog.vue'

const auth = useAuthStore()
const route = useRoute()
const router = useRouter()

const bookings = ref<Booking[]>([])
const otaBookings = ref<
  Array<{
    id: string
    property_id: string
    provider: string
    guest_name: string
    check_in: string
    check_out: string
  }>
>([])
const properties = ref<Property[]>([])
const loading = ref(false)
const saving = ref(false)
const viewMode = ref<'list' | 'calendar'>('list')
const dialog = ref(false)
const editingBooking = ref<Booking | null>(null)
const selectedDay = ref<Dayjs>()
const selectedDayBookings = ref<Booking[]>([])
const dayDrawer = ref(false)
const selectedDayLabel = ref('')
const searchQuery = ref('')
const statusFilter = ref<string>('')
const propertyFilter = ref<string>('')

const propertyFilterOptions = computed(() => [
  { value: '', label: 'All Properties' },
  ...(properties.value || []).map((p) => ({ value: p.id, label: p.title })),
])

const propertyMap = computed(() => {
  return new Map((properties.value || []).map((p) => [p.id, p]))
})

const companyPropertyIds = computed(() => {
  if (auth.isAdmin) return undefined
  return properties.value.filter((p) => p.company_id === auth.user?.company_id).map((p) => p.id)
})

const filteredBookings = computed(() => {
  let result = bookings.value

  if (companyPropertyIds.value) {
    result = result.filter((b) => companyPropertyIds.value?.includes(b.property_id))
  }

  const q = searchQuery.value.toLowerCase().trim()
  if (q) {
    result = result.filter(
      (b) =>
        b.guest_name.toLowerCase().includes(q) ||
        b.guest_email?.toLowerCase().includes(q) ||
        b.property_id.toLowerCase().includes(q) ||
        b.check_in.includes(q) ||
        b.check_out.includes(q),
    )
  }

  if (statusFilter.value) {
    result = result.filter((b) => b.status === statusFilter.value)
  }

  if (propertyFilter.value) {
    result = result.filter((b) => b.property_id === propertyFilter.value)
  }

  return result
})

const statusOptions = [
  { value: '', label: 'All Status' },
  { value: 'confirmed', label: 'Confirmed' },
  { value: 'pending', label: 'Pending' },
  { value: 'cancelled', label: 'Cancelled' },
  { value: 'completed', label: 'Completed' },
  { value: 'no-show', label: 'No Show' },
]

const canAdd = computed(() => auth.can('bookings', 'add'))

const statusColors: Record<string, string> = {
  confirmed: 'green',
  pending: 'orange',
  cancelled: 'red',
  completed: 'blue',
  'no-show': 'gray',
  airbnb: '#FF5A5F',
  booking: '#003580',
  vrbo: '#2E77AE',
  google: '#4285F4',
  other: '#666',
}

const loadData = async () => {
  loading.value = true
  try {
    const [bookingsRes, propertiesRes] = await Promise.all([fetchBookings(), fetchProperties()])
    bookings.value = bookingsRes
    properties.value = propertiesRes

    try {
      const otaResults = await Promise.all(
        propertiesRes.map((p) => fetchOtaBookings(p.id).catch(() => [])),
      )
      otaBookings.value = otaResults.flat()
    } catch {
      otaBookings.value = []
    }
  } catch (error: any) {
    message.error(error?.message || 'Failed to load bookings.')
  } finally {
    loading.value = false
  }
}

const loadPreference = async () => {
  if (!auth.user?.id) return
  try {
    const pref = await getPreference(auth.user.id)
    if (pref?.booking_view_mode) {
      viewMode.value = pref.booking_view_mode
    }
  } catch {
    // fallback to default
  }
}

const saveViewMode = async (mode: 'list' | 'calendar') => {
  if (!auth.user?.id) return
  try {
    await upsertPreference(auth.user.id, { booking_view_mode: mode })
  } catch {
    // silently fail
  }
}

const toggleView = (mode: 'list' | 'calendar') => {
  viewMode.value = mode
  saveViewMode(mode)
}

const openNewBooking = () => {
  if (!canAdd.value) {
    message.warning('You do not have permission to add bookings.')
    return
  }
  editingBooking.value = null
  dialog.value = true
}

const openEditBooking = (booking: Booking) => {
  editingBooking.value = { ...booking }
  dialog.value = true
}

const handleDelete = async (booking: Booking) => {
  try {
    await deleteBooking(booking.id)
    message.success('Booking deleted.')
    await loadData()
  } catch (error: any) {
    message.error(error?.message || 'Failed to delete booking.')
  }
}

const handleSave = async (payload: Partial<Booking>) => {
  saving.value = true
  try {
    if (editingBooking.value?.id) {
      await updateBooking(editingBooking.value.id, payload)
      message.success('Booking updated.')
    } else {
      await createBooking(payload)
      message.success('Booking created.')
    }
    dialog.value = false
    await loadData()
  } catch (error: any) {
    message.error(error?.message || 'Failed to save booking.')
  } finally {
    saving.value = false
  }
}

function getDateRange(start: string, end: string): string[] {
  const dates: string[] = []
  const current = new Date(start)
  const last = new Date(end)
  const maxDays = 365
  while (current <= last && dates.length < maxDays) {
    dates.push(current.toISOString().slice(0, 10))
    current.setDate(current.getDate() + 1)
  }
  return dates
}

const bookingsByDate = computed(() => {
  const map = new Map<string, any[]>()
  for (const b of filteredBookings.value) {
    const dates = getDateRange(b.check_in, b.check_out)
    for (const date of dates) {
      if (!map.has(date)) map.set(date, [])
      map.get(date)!.push({ ...b, _source: 'casamana' })
    }
  }
  for (const o of otaBookings.value) {
    if (!propertyMap.value.get(o.property_id)) continue
    const dates = getDateRange(o.check_in, o.check_out)
    for (const date of dates) {
      if (!map.has(date)) map.set(date, [])
      map.get(date)!.push({
        id: o.id,
        guest_name: o.guest_name,
        check_in: o.check_in,
        check_out: o.check_out,
        status: o.provider,
        property_id: o.property_id,
        _source: 'ota',
      })
    }
  }
  return map
})

const showDayBookings = (date: Dayjs) => {
  const key = date.format('YYYY-MM-DD')
  const dayBookings = bookingsByDate.value.get(key) || []
  selectedDayBookings.value = dayBookings
  selectedDayLabel.value = date.format('MMMM D, YYYY')
  dayDrawer.value = true
}

const columns = [
  { title: 'Guest', dataIndex: 'guest_name', key: 'guest_name', width: 180 },
  {
    title: 'Property',
    key: 'property',
    width: 200,
    customRender: ({ record }: { record: Booking }) => {
      const prop = propertyMap.value.get(record.property_id)
      return prop?.title || record.property_id
    },
  },
  {
    title: 'Check In',
    dataIndex: 'check_in',
    key: 'check_in',
    width: 120,
  },
  {
    title: 'Check Out',
    dataIndex: 'check_out',
    key: 'check_out',
    width: 120,
  },
  {
    title: 'Guests',
    dataIndex: 'guests_count',
    key: 'guests_count',
    width: 80,
    align: 'center' as const,
  },
  {
    title: 'Status',
    dataIndex: 'status',
    key: 'status',
    width: 120,
    customRender: ({ text }: { text: string }) => {
      return h('a-tag', { color: statusColors[text] || 'default' }, text)
    },
  },
  {
    title: 'Actions',
    key: 'actions',
    width: 160,
    customRender: ({ record }: { record: Booking }) => {
      const canEdit = auth.can(
        'bookings',
        'edit',
        propertyMap.value.get(record.property_id)?.company_id,
      )
      const canDelete = auth.can(
        'bookings',
        'delete',
        propertyMap.value.get(record.property_id)?.company_id,
      )
      return h('a-space', [
        canEdit
          ? h('a-button', { size: 'small', onClick: () => openEditBooking(record) }, 'Edit')
          : null,
        canDelete
          ? h(
              'a-popconfirm',
              {
                title: 'Delete Booking',
                description: 'Are you sure?',
                onConfirm: () => handleDelete(record),
              },
              () => h('a-button', { size: 'small', danger: true }, 'Delete'),
            )
          : null,
      ])
    },
  },
]

const openBookingFromQuery = () => {
  const bookingId = route.query.bookingId as string | undefined
  if (!bookingId || typeof bookingId !== 'string') return
  const booking = bookings.value.find((b) => b.id === bookingId)
  if (booking) {
    editingBooking.value = { ...booking }
    dialog.value = true
    // remove param so refresh doesn't reopen
    const { bookingId: _omit, ...rest } = route.query as any
    router.replace({ query: rest })
  }
}

watch(() => route.query.bookingId, openBookingFromQuery)
watch(bookings, openBookingFromQuery)

onMounted(async () => {
  await loadData()
  loadPreference()
  openBookingFromQuery()
})
</script>

<template>
  <a-flex style="margin-bottom: 16px" justify="space-between" gap="small" wrap="wrap" align="center">
    <a-radio-group
      :value="viewMode"
      button-style="solid"
      @change="(e: any) => toggleView(e.target.value)"
    >
      <a-radio-button value="list">
        <UnorderedListOutlined />
        List
      </a-radio-button>
      <a-radio-button value="calendar">
        <CalendarOutlined />
        Calendar
      </a-radio-button>
    </a-radio-group>

    <a-space>
      <a-input-search
        v-model:value="searchQuery"
        placeholder="Search by guest, email, or property..."
        allow-clear
        style="width: 260px"
      />
      <a-select v-model:value="statusFilter" :options="statusOptions" style="width: 140px" allow-clear />
      <a-select
        v-model:value="propertyFilter"
        :options="propertyFilterOptions"
        show-search
        :filter-option="(input: string, option: any) => option.label.toLowerCase().includes(input.toLowerCase())"
        style="width: 200px"
        allow-clear
      />
      <a-button @click="loadData" :loading="loading">Refresh</a-button>
      <a-button
        type="primary"
        :icon="h(PlusOutlined)"
        @click="openNewBooking"
        :disabled="!canAdd"
      >
        New Booking
      </a-button>
    </a-space>
  </a-flex>

  <a-spin :spinning="loading">
    <!-- List View -->
    <template v-if="viewMode === 'list'">
      <div
        v-if="filteredBookings.length === 0"
        style="display: flex; justify-content: center; align-items: center; min-height: 300px"
      >
        <a-empty description="No bookings found" />
      </div>
      <a-table
        v-else
        :columns="columns"
        :data-source="filteredBookings"
        :row-key="(r: Booking) => r.id"
        :pagination="{ pageSize: 20, showSizeChanger: true, pageSizeOptions: ['10', '20', '50'] }"
        size="middle"
        bordered
      />
    </template>

    <!-- Calendar View -->
    <template v-else>
      <a-calendar v-model:value="selectedDay" :fullscreen="true">
        <template #dateCellRender="{ current }">
          <div
            class="calendar-cell"
            role="button"
            tabindex="0"
            @click="showDayBookings(current)"
            @keydown.enter.prevent="showDayBookings(current)"
            @keydown.space.prevent="showDayBookings(current)"
          >
            <template v-if="bookingsByDate.has(current.format('YYYY-MM-DD'))">
              <div
                v-for="b in bookingsByDate.get(current.format('YYYY-MM-DD'))"
                :key="b.id"
                class="booking-chip"
                :style="{ borderLeft: `3px solid ${statusColors[b.status] || '#999'}` }"
                :title="`${b.guest_name} — ${b.check_in} → ${b.check_out}`"
                :aria-label="`${b.guest_name}, ${b.status}, ${b.check_in} to ${b.check_out}`"
              >
                <span class="booking-chip-guest">{{ b.guest_name }}</span>
                <span v-if="b.rental_type === 'monthly'" class="booking-chip-badge" style="background: #52c41a">M</span>
                <span v-else-if="b.rental_type === 'lease'" class="booking-chip-badge" style="background: #722ed1">L</span>
                <span v-if="b.check_in === current.format('YYYY-MM-DD')" class="booking-chip-badge">IN</span>
                <span v-else-if="b.check_out === current.format('YYYY-MM-DD')" class="booking-chip-badge out">OUT</span>
              </div>
            </template>
          </div>
        </template>
      </a-calendar>
    </template>
  </a-spin>

  <!-- Day bookings drawer -->
  <a-drawer
    :open="dayDrawer"
    :title="selectedDayLabel"
    placement="right"
    width="400"
    @close="dayDrawer = false"
  >
    <div v-if="selectedDayBookings.length === 0" style="text-align: center; padding: 48px 0">
      <a-empty description="No bookings this day" />
    </div>
    <a-list v-else :data-source="selectedDayBookings" :split="true">
      <template #renderItem="{ item }">
        <a-list-item>
          <a-list-item-meta>
            <template #title>
              <a-space>
                <span>{{ item.guest_name }}</span>
                <a-tag v-if="item._source === 'ota'" :color="statusColors[item.status] || 'gray'" style="font-size: 10px">
                  {{ item.status.toUpperCase() }}
                </a-tag>
                <a-tag v-else :color="statusColors[item.status] || 'gray'">{{ item.status }}</a-tag>
              </a-space>
            </template>
            <template #description>
              <div>
                <EnvironmentOutlined style="margin-right: 4px" />
                {{ propertyMap.get(item.property_id)?.title || item.property_id }}
              </div>
              <div style="margin-top: 4px">
                {{ item.check_in }} → {{ item.check_out }}
                <span v-if="item._source !== 'ota'">
                  · {{ item.guests_count || '?' }} guest{{ item.guests_count > 1 ? 's' : '' }}
                </span>
              </div>
              <div v-if="item._source === 'ota'" style="margin-top: 2px; font-size: 11px; color: var(--text-secondary)">
                Imported from {{ item.status }} — managed externally
              </div>
              <div v-else-if="item.notes" style="margin-top: 4px; color: var(--text-secondary)">{{ item.notes }}</div>
            </template>
          </a-list-item-meta>
          <template #actions>
            <template v-if="item._source !== 'ota'">
            <a-button
              v-if="auth.can('bookings', 'edit', propertyMap.get(item.property_id)?.company_id)"
              size="small"
              type="link"
              @click="openEditBooking(item)"
            >Edit</a-button>
            <a-popconfirm
              v-if="auth.can('bookings', 'delete', propertyMap.get(item.property_id)?.company_id)"
              title="Delete Booking?"
              description="Are you sure?"
              @confirm="handleDelete(item)"
            >
              <a-button size="small" type="link" danger>Delete</a-button>
            </a-popconfirm>
            </template>
          </template>
        </a-list-item>
      </template>
    </a-list>
  </a-drawer>

  <!-- Create / Edit Booking Modal -->
  <BookingFormDialog
    v-model="dialog"
    :booking="editingBooking"
    :properties="properties"
    :saving="saving"
    @save="handleSave"
  />
</template>

<style scoped>
.calendar-cell {
  min-height: 60px;
  cursor: pointer;
}

.booking-chip {
  font-size: 11px;
  line-height: 1.3;
  padding: 1px 4px;
  margin-bottom: 1px;
  border-radius: 2px;
  background: var(--bg-component, #fafafa);
  display: flex;
  gap: 4px;
  align-items: center;
  white-space: nowrap;
  overflow: hidden;
}

.booking-chip-guest {
  overflow: hidden;
  text-overflow: ellipsis;
  flex: 1;
  min-width: 0;
}

.booking-chip-badge {
  font-size: 9px;
  font-weight: 600;
  padding: 0 3px;
  border-radius: 2px;
  background: #52c41a;
  color: #fff;
  flex-shrink: 0;
}

.booking-chip-badge.out {
  background: #1677ff;
}
</style>
